import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Redeem.scss';
import { BackendRedeemItem, RedeemApiResponse } from 'types/dto/redeem';
import RedeemApply from './subviews/redeemApply/RedeemApply';
import { Button, Grid, Pagination, Skeleton } from 'antd';
import { drawTicketShape } from 'utils/canvasTicket';
import { getCssVar } from 'utils/commonUtils';
import { fetchRedeemItems } from 'services/redeemService';
import { fetchTier } from 'services/profileService';
import { MembershipTier } from 'types/dto/profile';
import { useSession } from 'context/SessionContext';

const REDEEM_PAGE_SIZE = 10;

const Redeem: React.FC = () => {
  const { t } = useTranslation('redeem');
  const { t: tCommon } = useTranslation('common');
  const [redeemItems, setRedeemItems] = useState<BackendRedeemItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<BackendRedeemItem>();
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [tier, setTier] = useState<MembershipTier>();
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const {session} = useSession();

  const reloadRedeemItems = useCallback(async (page: number) => {
    try {
      setIsLoading(true);
      setLoadError(false);

      const response: RedeemApiResponse = await fetchRedeemItems(page);
      const results = response?.results ?? [];
      const responseTotal = response?.total ?? 0;
      const lastPage = Math.max(1, Math.ceil(responseTotal / REDEEM_PAGE_SIZE));

      if (page > lastPage) {
        setCurrentPage(lastPage);
        return;
      }

      setRedeemItems(results);
      setTotal(responseTotal);
    } catch (error) {
      setRedeemItems([]);
      setTotal(0);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    reloadRedeemItems(currentPage);
  }, [currentPage, reloadRedeemItems]);

  useEffect(() => {
    const loadTier = async () => {
      try {
        const tierData: MembershipTier = await fetchTier('/profile');
        setTier(tierData);
      } catch (error) {
        setTier(undefined);
      }
    };

    loadTier();
  }, [session]);

  const handleRedeem = useCallback((item: BackendRedeemItem) => {
    setSelectedItem(item);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setSelectedItem(undefined);
  }, []);

  const handleRedeemSuccess = useCallback(() => {
    reloadRedeemItems(currentPage);
    setModalVisible(false);
    setSelectedItem(undefined);
  }, [currentPage, reloadRedeemItems]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const renderRedeemItem = (item: BackendRedeemItem) => {
    return <RedeemItemCard key={item.id} item={item} onRedeem={handleRedeem} />;
  };

  return (
    <>
      <div className="redeem-container">
        <div className="redeem-content">
          <div className="redeem-section">
            <div className="redeem-title">{t('pageTitle')}</div>

            {/* Show loading skeleton overlay while data is loading */}
            {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

            {/* Show error alert if loading failed */}
            {!isLoading && loadError && (
              <div>{tCommon('error.loadDataError')}</div>
            )}

            {/* Redeem content */}
            {!isLoading && !loadError && (
              <>
                {redeemItems.length > 0 ? (
                  <>
                    <div className="redeem-grid">
                      {redeemItems.map(renderRedeemItem)}
                    </div>
                    {total > REDEEM_PAGE_SIZE && (
                      <div className="redeem-pagination">
                        <Pagination
                          current={currentPage}
                          total={total}
                          pageSize={REDEEM_PAGE_SIZE}
                          showSizeChanger={false}
                          onChange={handlePageChange}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-message">{t('noItemsAvailable')}</div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <RedeemApply
        visible={modalVisible}
        onClose={handleCloseModal}
        onRedeemSuccess={handleRedeemSuccess}
        item={selectedItem}
        point={tier?.progress?.current ?? 0}
      />
    </>
  );
};

export interface RedeemItemCardProps {
  item: BackendRedeemItem;
  onRedeem: (item: BackendRedeemItem) => void;
  onClick?: (item: BackendRedeemItem) => void;
}

const RedeemItemCard: React.FC<RedeemItemCardProps> = ({
  item,
  onRedeem,
  onClick,
}) => {
  const { t, i18n } = useTranslation('redeem');
  const currentLanguage = i18n.language;
  const isEnglish = currentLanguage === 'en';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const screens = Grid.useBreakpoint();
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // Draw ticket shape on canvas
  useEffect(() => {
    const drawTicket = () => {
      if (!canvasRef.current || !containerRef.current) return;

      const container = containerRef.current;
      const canvas = canvasRef.current;
      const width = container.offsetWidth;
      const height = container.offsetHeight;

      if (width === 0 || height === 0) return;

      // High DPI support
      const scale = window.devicePixelRatio || 1;
      canvas.width = width * scale;
      canvas.height = height * scale;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.scale(scale, scale);

      drawTicketShape(ctx, {
        width,
        height,
        cornerRadius: screens.xs ? 6 : 10,
        notchRadius: screens.xs ? 12 : 18,
        notchPosition: 0.7,
        borderColor: getCssVar('--vls-grey-light'),
        backgroundColor: 'white',
        borderWidth: 2,
      });
    };

    drawTicket();
    window.addEventListener('resize', drawTicket);

    const resizeObserver = new ResizeObserver(() => drawTicket());
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', drawTicket);
      resizeObserver.disconnect();
    };
  }, [screens]);

  const handleCardClick = () => {
    if (onClick) {
      onClick(item);
    }
  };

  const handleRedeemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRedeem(item);
  };

  return (
    <div
      ref={containerRef}
      className="redeem-item-card"
      onClick={handleCardClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <canvas ref={canvasRef} className="redeem-item-card-canvas" />

      <div className="redeem-item-image">
        {imageLoading && (
          <Skeleton.Image active={true} className="image-skeleton" />
        )}
        {imageError && (
          <div className="image-placeholder">
            <i className="fas fa-image"></i>
          </div>
        )}
        <img
          src={isEnglish ? item.image.en : item.image.zh}
          onLoad={() => {
            setImageLoading(false);
            setImageError(false);
          }}
          onError={() => {
            setImageLoading(false);
            setImageError(true);
          }}
          style={{ display: imageLoading || imageError ? 'none' : 'block' }}
        />
      </div>

      <div className="redeem-item-info">
        <div className="redeem-item-content">
          <div className="redeem-item-title">
            {isEnglish ? item.title.en : item.title.zh}
          </div>
          {item.details && (
            <div className="redeem-item-description">
              {isEnglish ? item.details.en : item.details.zh}
            </div>
          )}
        </div>

        <div className="redeem-item-action" onClick={handleRedeemClick}>
          <Button type="link" className="redeem-button">
            {t('redeemButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Redeem;
